import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useFeatureConfig } from '../../../context/FeatureConfigContext';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS } from '../../../data/nurseAppModels';
import InspectionReminderWidget from './InspectionReminderWidget';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const EquipmentListPage = () => {
  const { equipment, catalogs, loading } = useEquipment();
  const { currentTeam } = useTeam();
  const { isFeatureEnabled } = useFeatureConfig();
  const { currentUser } = useAuth();
  const { getEquipmentNotificationCount, getEquipmentPendingActionsCount, notifications } = useNotification();
  const { getPendingInspections } = useEquipment();
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    // Load from localStorage or default to 'grid'
    const saved = localStorage.getItem('equipmentViewMode');
    return saved || 'grid';
  });
  const [repairNotificationCount, setRepairNotificationCount] = useState(0);
  const [repairPendingActionsCount, setRepairPendingActionsCount] = useState(0);
  
  // Member pending actions counts
  const [pendingInspectionsCount, setPendingInspectionsCount] = useState(0);
  const [myInspectionsCount, setMyInspectionsCount] = useState(0);
  const [myBorrowsCount, setMyBorrowsCount] = useState(0);
  const [myRepairsCount, setMyRepairsCount] = useState(0);
  
  // Pending approvals counts (แยกตามประเภท)
  const [myInspectionsPendingApproval, setMyInspectionsPendingApproval] = useState(0);
  const [myBorrowsPendingApproval, setMyBorrowsPendingApproval] = useState(0);
  const [myReturnsPendingApproval, setMyReturnsPendingApproval] = useState(0);
  const [myRepairsPendingApproval, setMyRepairsPendingApproval] = useState(0);
  const [myRepairCompletionsPendingApproval, setMyRepairCompletionsPendingApproval] = useState(0);
  
  // Leader pending equipment approvals count
  const [pendingEquipmentApprovalCount, setPendingEquipmentApprovalCount] = useState(0);
  
  const isLeader = currentTeam?.userRole === 'LEADER' || currentTeam?.userRole === 'DEPUTY_LEADER';

  // Load repair notification count and pending actions count for leaders
  useEffect(() => {
    if (!currentTeam || !isLeader || !getEquipmentNotificationCount || !currentUser) return;
    
    const userRole = currentTeam.userRole || 'MEMBER';
    const count = getEquipmentNotificationCount(userRole);
    
    // Filter only repair-related notifications
    const repairNotificationTypes = [
      'equipment_repair_requested',
      'equipment_repair_receive_requested',
      'equipment_repair_completion_requested'
    ];
    const repairCount = notifications.filter(notif => 
      !notif.read && repairNotificationTypes.includes(notif.type)
    ).length;
    
    setRepairNotificationCount(repairCount);

        // Load repair-related pending actions count
        const loadRepairPendingActions = async () => {
          try {
            // Pending repair requests
            const repairsQuery = query(
              collection(db, 'equipmentRepairs'),
              where('teamId', '==', currentTeam.id),
              where('status', '==', 'PENDING')
            );
            const repairsSnapshot = await getDocs(repairsQuery);
            
            // Pending repair receive requests
            const repairReceivesQuery = query(
              collection(db, 'equipmentRepairs'),
              where('teamId', '==', currentTeam.id),
              where('receiveStatus', '==', 'PENDING')
            );
            const repairReceivesSnapshot = await getDocs(repairReceivesQuery);
            
            // Pending repair completion requests
            const repairCompletionsQuery = query(
              collection(db, 'equipmentRepairs'),
              where('teamId', '==', currentTeam.id),
              where('completionRequestStatus', '==', 'PENDING')
            );
            const repairCompletionsSnapshot = await getDocs(repairCompletionsQuery);
            
            const repairActionsCount = repairsSnapshot.size + repairReceivesSnapshot.size + repairCompletionsSnapshot.size;
            console.log('📋 EquipmentListPage: Repair pending actions count:', {
              repairs: repairsSnapshot.size,
              receives: repairReceivesSnapshot.size,
              completions: repairCompletionsSnapshot.size,
              total: repairActionsCount
            });
            setRepairPendingActionsCount(repairActionsCount);
          } catch (err) {
            console.error('Error loading repair pending actions count:', err);
            setRepairPendingActionsCount(0);
          }
        };

        loadRepairPendingActions();

        // Load pending equipment approvals count for leaders
        const loadPendingEquipmentApprovals = async () => {
          if (!currentTeam || !isLeader) return;
          try {
            const equipmentQuery = query(
              collection(db, 'equipment'),
              where('teamId', '==', currentTeam.id),
              where('status', '==', 'PENDING_APPROVAL')
            );
            const equipmentSnapshot = await getDocs(equipmentQuery);
            console.log('📋 EquipmentListPage: Pending equipment approvals:', equipmentSnapshot.size);
            setPendingEquipmentApprovalCount(equipmentSnapshot.size);
          } catch (err) {
            console.error('Error loading pending equipment approvals:', err);
            setPendingEquipmentApprovalCount(0);
          }
        };

        loadRepairPendingActions();
        loadPendingEquipmentApprovals();
  }, [currentTeam, isLeader, currentUser, getEquipmentNotificationCount, getEquipmentPendingActionsCount, notifications]);

  // Load member pending actions counts
  useEffect(() => {
    if (!currentTeam || !currentUser || !getPendingInspections || !getEquipmentPendingActionsCount) return;

    const loadMemberPendingActions = async () => {
      try {
        const userRole = currentTeam.userRole || 'MEMBER';
        
        // 1. Pending inspections (ต้องตรวจสอบ) - ใช้ getPendingInspections
        console.log('🔍 EquipmentListPage: Getting pending inspections...');
        const pendingInspections = await getPendingInspections();
        console.log('✅ EquipmentListPage: Pending inspections result:', {
          count: pendingInspections.length,
          inspections: pendingInspections.map(insp => ({
            round: insp.round,
            status: insp.status,
            shiftType: insp.shiftType,
            inspectionId: insp.inspectionId
          }))
        });
        
        // Filter only NOT_STARTED, IN_PROGRESS, OVERDUE (not COMPLETED)
        const activePendingInspections = pendingInspections.filter(insp => 
          ['NOT_STARTED', 'IN_PROGRESS', 'OVERDUE'].includes(insp.status)
        );
        console.log('✅ EquipmentListPage: Active pending inspections (NOT_STARTED/IN_PROGRESS/OVERDUE):', {
          count: activePendingInspections.length,
          inspections: activePendingInspections.map(insp => ({
            round: insp.round,
            status: insp.status,
            shiftType: insp.shiftType
          }))
        });
        
        setPendingInspectionsCount(activePendingInspections.length);

        // 2. My inspections ที่ยังไม่เสร็จ (DRAFT, IN_PROGRESS)
        try {
          console.log('🔍 EquipmentListPage: Querying my inspections:', {
            teamId: currentTeam.id,
            inspectedBy: currentUser.uid,
            status: ['DRAFT', 'IN_PROGRESS']
          });
          const myInspectionsQuery = query(
            collection(db, 'equipmentInspections'),
            where('teamId', '==', currentTeam.id),
            where('inspectedBy', '==', currentUser.uid),
            where('status', 'in', ['DRAFT', 'IN_PROGRESS'])
          );
          const myInspectionsSnapshot = await getDocs(myInspectionsQuery);
          console.log('✅ EquipmentListPage: My inspections count:', myInspectionsSnapshot.size);
          setMyInspectionsCount(myInspectionsSnapshot.size);
        } catch (err) {
          console.error('❌ EquipmentListPage: Error loading my inspections:', err);
          if (err.code === 'failed-precondition') {
            console.error('⚠️ EquipmentListPage: Missing index for my inspections query');
            console.error('Required index: equipmentInspections - teamId (ASC) + inspectedBy (ASC) + status (ASC)');
            console.error('Index URL:', err.message?.match(/https:\/\/[^\s]+/)?.[0] || 'N/A');
          }
          setMyInspectionsCount(0);
        }
        
        // 2b. My inspections pending approval
        try {
          const myInspectionsPendingQuery = query(
            collection(db, 'equipmentInspections'),
            where('teamId', '==', currentTeam.id),
            where('inspectedBy', '==', currentUser.uid),
            where('status', '==', 'PENDING_APPROVAL')
          );
          const myInspectionsPendingSnapshot = await getDocs(myInspectionsPendingQuery);
          console.log('✅ EquipmentListPage: My inspections pending approval:', myInspectionsPendingSnapshot.size);
          setMyInspectionsPendingApproval(myInspectionsPendingSnapshot.size);
        } catch (err) {
          console.error('❌ EquipmentListPage: Error loading my inspections pending approval:', err);
          setMyInspectionsPendingApproval(0);
        }

        // 3. My borrowed equipment ที่ต้อง return (APPROVED และ returnStatus != 'COMPLETED')
        try {
          console.log('🔍 EquipmentListPage: Querying my borrows:', {
            teamId: currentTeam.id,
            borrowedBy: currentUser.uid,
            status: 'APPROVED'
          });
          // Query borrows ที่ approved แล้ว (ไม่นับ returnStatus เพราะอาจจะยังไม่ return หรือ return แล้ว)
          const myBorrowsQuery = query(
            collection(db, 'equipmentBorrows'),
            where('teamId', '==', currentTeam.id),
            where('borrowedBy', '==', currentUser.uid),
            where('status', '==', 'APPROVED')
          );
          const myBorrowsSnapshot = await getDocs(myBorrowsQuery);
          // Filter out items that are already returned (returnStatus === 'COMPLETED')
          const activeBorrows = myBorrowsSnapshot.docs.filter(doc => {
            const data = doc.data();
            return data.returnStatus !== 'COMPLETED';
          });
          console.log('✅ EquipmentListPage: My borrows count (approved, not returned):', activeBorrows.length);
          setMyBorrowsCount(activeBorrows.length);
        } catch (err) {
          console.error('❌ EquipmentListPage: Error loading my borrows:', err);
          if (err.code === 'failed-precondition') {
            console.error('⚠️ EquipmentListPage: Missing index for my borrows query');
            console.error('Required index: equipmentBorrows - teamId (ASC) + borrowedBy (ASC) + status (ASC)');
            console.error('Index URL:', err.message?.match(/https:\/\/[^\s]+/)?.[0] || 'N/A');
          }
          setMyBorrowsCount(0);
        }
        
        // 3b. My borrow requests pending approval
        try {
          const myBorrowsPendingQuery = query(
            collection(db, 'equipmentBorrows'),
            where('teamId', '==', currentTeam.id),
            where('borrowedBy', '==', currentUser.uid),
            where('status', '==', 'PENDING')
          );
          const myBorrowsPendingSnapshot = await getDocs(myBorrowsPendingQuery);
          console.log('✅ EquipmentListPage: My borrows pending approval:', myBorrowsPendingSnapshot.size);
          setMyBorrowsPendingApproval(myBorrowsPendingSnapshot.size);
        } catch (err) {
          console.error('❌ EquipmentListPage: Error loading my borrows pending approval:', err);
          setMyBorrowsPendingApproval(0);
        }
        
        // 3c. My return requests pending approval (เฉพาะที่ status='APPROVED' และ returnStatus='PENDING')
        try {
          const myReturnsPendingQuery = query(
            collection(db, 'equipmentBorrows'),
            where('teamId', '==', currentTeam.id),
            where('borrowedBy', '==', currentUser.uid),
            where('status', '==', 'APPROVED'),
            where('returnStatus', '==', 'PENDING')
          );
          const myReturnsPendingSnapshot = await getDocs(myReturnsPendingQuery);
          console.log('✅ EquipmentListPage: My returns pending approval:', myReturnsPendingSnapshot.size);
          setMyReturnsPendingApproval(myReturnsPendingSnapshot.size);
        } catch (err) {
          console.error('❌ EquipmentListPage: Error loading my returns pending approval:', err);
          if (err.code === 'failed-precondition') {
            console.error('⚠️ EquipmentListPage: Missing index for my returns query');
            console.error('Required index: equipmentBorrows - teamId (ASC) + borrowedBy (ASC) + status (ASC) + returnStatus (ASC)');
            console.error('Index URL:', err.message?.match(/https:\/\/[^\s]+/)?.[0] || 'N/A');
          }
          setMyReturnsPendingApproval(0);
        }

        // 4. My repair requests ที่รอ approval (status='PENDING')
        try {
          console.log('🔍 EquipmentListPage: Querying my repairs pending approval:', {
            teamId: currentTeam.id,
            requestedBy: currentUser.uid,
            status: 'PENDING'
          });
          // Try requestedBy first
          let myRepairsPendingCount = 0;
          try {
            const myRepairsQuery = query(
              collection(db, 'equipmentRepairs'),
              where('teamId', '==', currentTeam.id),
              where('requestedBy', '==', currentUser.uid),
              where('status', '==', 'PENDING')
            );
            const myRepairsSnapshot = await getDocs(myRepairsQuery);
            console.log('✅ EquipmentListPage: My repairs pending approval (requestedBy):', myRepairsSnapshot.size);
            myRepairsPendingCount = myRepairsSnapshot.size;
          } catch (err1) {
            console.warn('⚠️ EquipmentListPage: requestedBy query failed:', err1);
          }
          
          // Also try reportedBy (for compatibility)
          let reportedByCount = 0;
          try {
            const myRepairsQuery2 = query(
              collection(db, 'equipmentRepairs'),
              where('teamId', '==', currentTeam.id),
              where('reportedBy', '==', currentUser.uid),
              where('status', '==', 'PENDING')
            );
            const myRepairsSnapshot2 = await getDocs(myRepairsQuery2);
            console.log('✅ EquipmentListPage: My repairs pending approval (reportedBy):', myRepairsSnapshot2.size);
            reportedByCount = myRepairsSnapshot2.size;
          } catch (err2) {
            console.warn('⚠️ EquipmentListPage: reportedBy query failed:', err2);
          }
          
          // Use the larger count (to avoid duplicates)
          const finalPendingCount = Math.max(myRepairsPendingCount, reportedByCount);
          setMyRepairsPendingApproval(finalPendingCount);
          // Note: myRepairsCount is not used for badge, only myRepairsPendingApproval
          setMyRepairsCount(0); // Clear this as it's not needed
        } catch (err) {
          console.error('❌ EquipmentListPage: Error loading my repairs pending approval:', err);
          setMyRepairsCount(0);
          setMyRepairsPendingApproval(0);
        }
        
        // 4b. My repair completion requests pending approval
        try {
          const myRepairCompletionsQuery = query(
            collection(db, 'equipmentRepairs'),
            where('teamId', '==', currentTeam.id),
            where('reportedBy', '==', currentUser.uid),
            where('completionRequestStatus', '==', 'PENDING')
          );
          const myRepairCompletionsSnapshot = await getDocs(myRepairCompletionsQuery);
          console.log('✅ EquipmentListPage: My repair completions pending approval:', myRepairCompletionsSnapshot.size);
          setMyRepairCompletionsPendingApproval(myRepairCompletionsSnapshot.size);
        } catch (err) {
          console.error('❌ EquipmentListPage: Error loading my repair completions pending approval:', err);
          setMyRepairCompletionsPendingApproval(0);
        }

        // Log for debugging
        console.log('📋 EquipmentListPage: Member pending actions summary:', {
          pendingInspections: activePendingInspections.length,
          pendingInspectionsDetails: activePendingInspections.map(insp => ({
            round: insp.round,
            status: insp.status,
            shiftType: insp.shiftType
          })),
          myInspections: myInspectionsCount,
          myInspectionsPendingApproval: myInspectionsPendingApproval,
          myBorrows: myBorrowsCount,
          myBorrowsPendingApproval: myBorrowsPendingApproval,
          myReturnsPendingApproval: myReturnsPendingApproval,
          myRepairs: myRepairsCount,
          myRepairsPendingApproval: myRepairsPendingApproval,
          myRepairCompletionsPendingApproval: myRepairCompletionsPendingApproval,
          badgeCounts: {
            inspection: activePendingInspections.length,
            myInspections: myInspectionsCount + myInspectionsPendingApproval,
            myBorrows: myBorrowsCount + myReturnsPendingApproval, // borrows ที่ต้อง return + return requests รออนุมัติ (ไม่รวม borrow requests รออนุมัติ)
            myRepairs: myRepairsPendingApproval + myRepairCompletionsPendingApproval // repair requests รออนุมัติ + completion requests รออนุมัติ
          },
          totalBadgeCount: activePendingInspections.length + 
            (myInspectionsCount + myInspectionsPendingApproval) + 
            (myBorrowsCount + myReturnsPendingApproval) + // ไม่รวม myBorrowsPendingApproval เพราะยังไม่ได้ยืม
            (myRepairsPendingApproval + myRepairCompletionsPendingApproval)
        });
      } catch (err) {
        console.error('Error loading member pending actions count:', err);
      }
    };

    loadMemberPendingActions();
  }, [currentTeam, currentUser, getPendingInspections, getEquipmentPendingActionsCount]);

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  const filteredEquipment = equipment.filter(eq => {
    const matchesFilter = filter === 'all' || 
      (filter === 'small' && eq.type === EQUIPMENT_TYPES.SMALL_ITEM) ||
      (filter === 'large' && eq.type === EQUIPMENT_TYPES.LARGE_ITEM);
    const matchesSearch = !searchTerm || 
      eq.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eq.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">อุปกรณ์</h1>
          <div className="flex gap-2">
            {isFeatureEnabled('equipmentManagement') && (
              <>
                <Link
                  to="/nurse/equipment/catalog"
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  Catalog
                </Link>
                <Link
                  to="/nurse/equipment/create"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  + เพิ่มอุปกรณ์
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Inspection Reminder Banner */}
        <div className="mb-6">
          <InspectionReminderWidget compact={false} />
        </div>

        {/* Filters and View Mode */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col gap-4">
            {/* Search and Filter Row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="ค้นหาอุปกรณ์..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 sm:w-auto w-full"
              >
                <option value="all">ทั้งหมด</option>
                <option value="small">ชิ้นเล็ก</option>
                <option value="large">ชิ้นใหญ่</option>
              </select>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-sm text-gray-600">มุมมอง:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setViewMode('grid');
                    localStorage.setItem('equipmentViewMode', 'grid');
                  }}
                  className={`p-2 rounded-lg transition ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="มุมมองกริด"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setViewMode('list');
                    localStorage.setItem('equipmentViewMode', 'list');
                  }}
                  className={`p-2 rounded-lg transition ${
                    viewMode === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="มุมมองรายการ"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Equipment List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        ) : filteredEquipment.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">🩺</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">ยังไม่มีอุปกรณ์</h3>
            <p className="text-gray-600 mb-4">เพิ่มอุปกรณ์ใหม่หรือสร้างจาก Catalog</p>
            <Link
              to="/nurse/equipment/create"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              เพิ่มอุปกรณ์
            </Link>
          </div>
        ) : viewMode === 'grid' ? (
          // Grid View
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEquipment.map(eq => {
              // Get first image URL if available
              let firstImage = null;
              
              // Check images array
              if (eq.images && Array.isArray(eq.images) && eq.images.length > 0) {
                const firstImageItem = eq.images[0];
                firstImage = typeof firstImageItem === 'string' ? firstImageItem : (firstImageItem?.url || firstImageItem);
                
                // Debug log for images
                console.log('EquipmentListPage - Grid View (WITH IMAGES):', {
                  id: eq.id,
                  name: eq.name,
                  images: eq.images,
                  imagesLength: eq.images.length,
                  firstImageItem: firstImageItem,
                  firstImageItemType: typeof firstImageItem,
                  firstImageItemUrl: firstImageItem?.url,
                  thumbnail: eq.thumbnail,
                  firstImage: firstImage,
                  firstImageType: typeof firstImage,
                  willShowImage: !!firstImage
                });
              }
              
              // Fallback to thumbnail
              if (!firstImage && eq.thumbnail) {
                firstImage = eq.thumbnail;
                console.log('EquipmentListPage - Grid View (USING THUMBNAIL):', {
                  id: eq.id,
                  name: eq.name,
                  thumbnail: eq.thumbnail,
                  firstImage: firstImage
                });
              }
              
              return (
                <Link
                  key={eq.id}
                  to={`/nurse/equipment/${eq.id}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition overflow-hidden flex flex-col"
                >
                  {/* Equipment Image */}
                  {firstImage ? (
                    <div className="w-full h-40 sm:h-48 bg-gray-100 overflow-hidden">
                      <img
                        src={firstImage}
                        alt={eq.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('bg-gray-200');
                          e.target.parentElement.innerHTML = '<span class="text-gray-400 text-4xl flex items-center justify-center h-full">🩺</span>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-40 sm:h-48 bg-gray-200 flex items-center justify-center">
                      <span className="text-4xl text-gray-400">🩺</span>
                    </div>
                  )}
                  
                  <div className="p-4 sm:p-6 flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-bold text-gray-800 truncate">{eq.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">{eq.code}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded flex-shrink-0 ml-2 ${
                        eq.type === EQUIPMENT_TYPES.SMALL_ITEM 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {eq.type === EQUIPMENT_TYPES.SMALL_ITEM ? 'ชิ้นเล็ก' : 'ชิ้นใหญ่'}
                      </span>
                    </div>
                    <div className="space-y-2 mt-auto">
                      {eq.type === EQUIPMENT_TYPES.SMALL_ITEM ? (
                        <div className="flex items-center justify-between text-xs sm:text-sm">
                          <span className="text-gray-600">Stock:</span>
                          <span className="font-semibold text-gray-800">{eq.currentStock || 0} {eq.unit}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between text-xs sm:text-sm">
                          <span className="text-gray-600">สถานะ:</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            eq.status === EQUIPMENT_STATUS.PENDING_APPROVAL ? 'bg-yellow-100 text-yellow-700' :
                            eq.status === EQUIPMENT_STATUS.REJECTED ? 'bg-red-100 text-red-700' :
                            eq.status === EQUIPMENT_STATUS.AVAILABLE ? 'bg-green-100 text-green-700' :
                            eq.status === EQUIPMENT_STATUS.IN_USE ? 'bg-blue-100 text-blue-700' :
                            eq.status === EQUIPMENT_STATUS.MAINTENANCE ? 'bg-yellow-100 text-yellow-700' :
                            eq.status === EQUIPMENT_STATUS.BORROWED ? 'bg-purple-100 text-purple-700' :
                            eq.status === EQUIPMENT_STATUS.REPAIR ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {eq.status === EQUIPMENT_STATUS.PENDING_APPROVAL ? '⏳ รออนุมัติ' :
                             eq.status === EQUIPMENT_STATUS.REJECTED ? '❌ ถูกปฏิเสธ' :
                             eq.status === EQUIPMENT_STATUS.AVAILABLE ? 'พร้อมใช้' :
                             eq.status === EQUIPMENT_STATUS.IN_USE ? 'กำลังใช้งาน' :
                             eq.status === EQUIPMENT_STATUS.MAINTENANCE ? 'บำรุงรักษา' :
                             eq.status === EQUIPMENT_STATUS.BORROWED ? 'ถูกยืม' :
                             eq.status === EQUIPMENT_STATUS.REPAIR ? 'อยู่ระหว่างส่งซ่อม' :
                             eq.status}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          // List View
          <div className="space-y-3">
            {filteredEquipment.map(eq => {
              // Get first image URL if available
              let firstImage = null;
              
              // Check images array
              if (eq.images && Array.isArray(eq.images) && eq.images.length > 0) {
                const firstImageItem = eq.images[0];
                firstImage = typeof firstImageItem === 'string' ? firstImageItem : (firstImageItem?.url || firstImageItem);
                
                // Debug log for images
                console.log('EquipmentListPage - List View (WITH IMAGES):', {
                  id: eq.id,
                  name: eq.name,
                  images: eq.images,
                  imagesLength: eq.images.length,
                  firstImageItem: firstImageItem,
                  firstImageItemType: typeof firstImageItem,
                  firstImageItemUrl: firstImageItem?.url,
                  thumbnail: eq.thumbnail,
                  firstImage: firstImage,
                  firstImageType: typeof firstImage,
                  willShowImage: !!firstImage
                });
              }
              
              // Fallback to thumbnail
              if (!firstImage && eq.thumbnail) {
                firstImage = eq.thumbnail;
                console.log('EquipmentListPage - List View (USING THUMBNAIL):', {
                  id: eq.id,
                  name: eq.name,
                  thumbnail: eq.thumbnail,
                  firstImage: firstImage
                });
              }
              
              return (
                <Link
                  key={eq.id}
                  to={`/nurse/equipment/${eq.id}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition p-4 flex items-center gap-4"
                >
                  {/* Equipment Image */}
                  {firstImage ? (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={firstImage}
                        alt={eq.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('bg-gray-200');
                          e.target.parentElement.innerHTML = '<span class="text-gray-400 text-2xl flex items-center justify-center h-full">🩺</span>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg bg-gray-200 flex items-center justify-center">
                      <span className="text-2xl text-gray-400">🩺</span>
                    </div>
                  )}
                  
                  {/* Equipment Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-bold text-gray-800 truncate">{eq.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">{eq.code}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded flex-shrink-0 ml-2 ${
                        eq.type === EQUIPMENT_TYPES.SMALL_ITEM 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {eq.type === EQUIPMENT_TYPES.SMALL_ITEM ? 'ชิ้นเล็ก' : 'ชิ้นใหญ่'}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm">
                      {eq.type === EQUIPMENT_TYPES.SMALL_ITEM ? (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-600">Stock:</span>
                          <span className="font-semibold text-gray-800">{eq.currentStock || 0} {eq.unit || ''}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-600">สถานะ:</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            eq.status === EQUIPMENT_STATUS.PENDING_APPROVAL ? 'bg-yellow-100 text-yellow-700' :
                            eq.status === EQUIPMENT_STATUS.REJECTED ? 'bg-red-100 text-red-700' :
                            eq.status === EQUIPMENT_STATUS.AVAILABLE ? 'bg-green-100 text-green-700' :
                            eq.status === EQUIPMENT_STATUS.IN_USE ? 'bg-blue-100 text-blue-700' :
                            eq.status === EQUIPMENT_STATUS.MAINTENANCE ? 'bg-yellow-100 text-yellow-700' :
                            eq.status === EQUIPMENT_STATUS.REPAIR ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {eq.status === EQUIPMENT_STATUS.PENDING_APPROVAL ? '⏳ รออนุมัติ' :
                             eq.status === EQUIPMENT_STATUS.REJECTED ? '❌ ถูกปฏิเสธ' :
                             eq.status === EQUIPMENT_STATUS.AVAILABLE ? 'พร้อมใช้' :
                             eq.status === EQUIPMENT_STATUS.IN_USE ? 'กำลังใช้งาน' :
                             eq.status === EQUIPMENT_STATUS.MAINTENANCE ? 'บำรุงรักษา' :
                             eq.status === EQUIPMENT_STATUS.REPAIR ? 'อยู่ระหว่างส่งซ่อม' :
                             eq.status}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Arrow Icon */}
                  <div className="flex-shrink-0 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-6 space-y-6">
          {/* สำหรับสมาชิกทั่วไป */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              📋 สำหรับสมาชิก
              {/* Show total count in header */}
              {(pendingInspectionsCount + 
                (myInspectionsCount + myInspectionsPendingApproval) + 
                (myBorrowsCount + myReturnsPendingApproval) + // ไม่รวม myBorrowsPendingApproval เพราะยังไม่ได้ยืม
                (myRepairsPendingApproval + myRepairCompletionsPendingApproval)) > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {(pendingInspectionsCount + 
                    (myInspectionsCount + myInspectionsPendingApproval) + 
                    (myBorrowsCount + myReturnsPendingApproval) + 
                    (myRepairsPendingApproval + myRepairCompletionsPendingApproval)) > 9 
                    ? '9+' 
                    : (pendingInspectionsCount + 
                       (myInspectionsCount + myInspectionsPendingApproval) + 
                       (myBorrowsCount + myReturnsPendingApproval) + 
                       (myRepairsPendingApproval + myRepairCompletionsPendingApproval))}
                </span>
              )}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/nurse/equipment/inspection"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center relative"
              >
                {pendingInspectionsCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center z-10 shadow-lg animate-pulse">
                    {pendingInspectionsCount > 9 ? '9+' : pendingInspectionsCount}
                  </span>
                )}
                <div className="text-4xl mb-2">🔍</div>
                <h3 className="font-semibold text-gray-800">ตรวจสอบอุปกรณ์</h3>
                <p className="text-sm text-gray-600 mt-2">ตรวจสอบอุปกรณ์ 6 รอบต่อวัน</p>
              </Link>
              <Link
                to="/nurse/equipment/my-inspections"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center relative"
              >
                {(myInspectionsCount + myInspectionsPendingApproval) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center z-10 shadow-lg">
                    {(myInspectionsCount + myInspectionsPendingApproval) > 9 ? '9+' : (myInspectionsCount + myInspectionsPendingApproval)}
                  </span>
                )}
                <div className="text-4xl mb-2">📋</div>
                <h3 className="font-semibold text-gray-800">รายการตรวจสอบของฉัน</h3>
                <p className="text-sm text-gray-600 mt-2">ดูและจัดการรายการตรวจสอบ</p>
              </Link>
              <Link
                to="/nurse/equipment/borrow"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center relative"
              >
                {myBorrowsPendingApproval > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center z-10 shadow-lg">
                    {myBorrowsPendingApproval > 9 ? '9+' : myBorrowsPendingApproval}
                  </span>
                )}
                <div className="text-4xl mb-2">📦</div>
                <h3 className="font-semibold text-gray-800">ยืมอุปกรณ์</h3>
                <p className="text-sm text-gray-600 mt-2">ยืมอุปกรณ์ชิ้นใหญ่</p>
              </Link>
              <Link
                to="/nurse/equipment/my-borrows"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center relative"
              >
                {(myBorrowsCount + myReturnsPendingApproval) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center z-10 shadow-lg">
                    {(myBorrowsCount + myReturnsPendingApproval) > 9 
                      ? '9+' 
                      : (myBorrowsCount + myReturnsPendingApproval)}
                  </span>
                )}
                <div className="text-4xl mb-2">📋</div>
                <h3 className="font-semibold text-gray-800">อุปกรณ์ที่ฉันยืม</h3>
                <p className="text-sm text-gray-600 mt-2">ดูและจัดการอุปกรณ์ที่ยืม</p>
              </Link>
              <Link
                to="/nurse/equipment/repair"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center relative"
              >
                {myRepairsPendingApproval > 0 && (
                  <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center z-10 shadow-lg">
                    {myRepairsPendingApproval > 9 ? '9+' : myRepairsPendingApproval}
                  </span>
                )}
                <div className="text-4xl mb-2">🔧</div>
                <h3 className="font-semibold text-gray-800">ส่งซ่อมอุปกรณ์</h3>
                <p className="text-sm text-gray-600 mt-2">ส่งอุปกรณ์ไปซ่อม</p>
              </Link>
              <Link
                to="/nurse/equipment/my-repairs"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center relative"
              >
                {(myRepairsPendingApproval + myRepairCompletionsPendingApproval) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center z-10 shadow-lg">
                    {(myRepairsPendingApproval + myRepairCompletionsPendingApproval) > 9 
                      ? '9+' 
                      : (myRepairsPendingApproval + myRepairCompletionsPendingApproval)}
                  </span>
                )}
                <div className="text-4xl mb-2">📋</div>
                <h3 className="font-semibold text-gray-800">อุปกรณ์ที่ฉันส่งซ่อม</h3>
                <p className="text-sm text-gray-600 mt-2">ดูและจัดการรายการส่งซ่อม</p>
              </Link>
              <Link
                to="/nurse/equipment/stock"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
              >
                <div className="text-4xl mb-2">📥</div>
                <h3 className="font-semibold text-gray-800">จัดการ Stock</h3>
                <p className="text-sm text-gray-600 mt-2">นำเข้า Stock และอนุมัติ</p>
              </Link>
            </div>
          </div>

          {/* สำหรับหัวหน้าทีม */}
          {isLeader && (
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                👔 สำหรับหัวหน้าทีม
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Leader Only</span>
              </h2>
              
              {/* Equipment Approval Card */}
              <Link
                to="/nurse/equipment/approval"
                className="block bg-white rounded-lg shadow-md p-6 mb-4 hover:shadow-lg transition relative"
              >
                {/* Badge for pending equipment approvals */}
                {pendingEquipmentApprovalCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center z-10 shadow-lg animate-pulse">
                    {pendingEquipmentApprovalCount > 9 ? '9+' : pendingEquipmentApprovalCount}
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      ⏳ อนุมัติอุปกรณ์
                    </h3>
                    <p className="text-sm text-gray-600">
                      รายการอุปกรณ์ที่รอการอนุมัติและถูกปฏิเสธ
                    </p>
                  </div>
                  <div className="text-blue-600 text-xl">→</div>
                </div>
              </Link>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link
                  to="/nurse/equipment/inspections/approval"
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center border-2 border-yellow-300 relative"
                >
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">อนุมัติ</div>
                  <div className="text-4xl mb-2">✅</div>
                  <h3 className="font-semibold text-gray-800">อนุมัติผลการตรวจสอบ</h3>
                  <p className="text-sm text-gray-600 mt-2">รีวิวและอนุมัติผลการตรวจสอบ</p>
                </Link>
                <Link
                  to="/nurse/equipment/borrows/management"
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center border-2 border-yellow-300 relative"
                >
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">อนุมัติ</div>
                  <div className="text-4xl mb-2">📦</div>
                  <h3 className="font-semibold text-gray-800">จัดการการยืม</h3>
                  <p className="text-sm text-gray-600 mt-2">อนุมัติและจัดการคำขอยืม</p>
                </Link>
                <Link
                  to="/nurse/equipment/repairs/management"
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center border-2 border-yellow-300 relative"
                >
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">อนุมัติ</div>
                  {/* Notification Badge - Show pending actions count (to-do list) */}
                  {repairPendingActionsCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center z-10 shadow-lg animate-pulse">
                      {repairPendingActionsCount > 9 ? '9+' : repairPendingActionsCount}
                    </span>
                  )}
                  <div className="text-4xl mb-2">🔧</div>
                  <h3 className="font-semibold text-gray-800">จัดการการส่งซ่อม</h3>
                  <p className="text-sm text-gray-600 mt-2">อนุมัติและจัดการคำขอส่งซ่อม</p>
                </Link>
                <Link
                  to="/nurse/equipment/stock"
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center border-2 border-yellow-300 relative"
                >
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">อนุมัติ</div>
                  <div className="text-4xl mb-2">📥</div>
                  <h3 className="font-semibold text-gray-800">อนุมัติ Stock</h3>
                  <p className="text-sm text-gray-600 mt-2">อนุมัติ Stock In/Out</p>
                </Link>
                <Link
                  to="/nurse/equipment/dashboard"
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
                >
                  <div className="text-4xl mb-2">📊</div>
                  <h3 className="font-semibold text-gray-800">Dashboard</h3>
                  <p className="text-sm text-gray-600 mt-2">สรุปยอดอุปกรณ์รายวัน</p>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquipmentListPage;


